import { DATA_VIEW_FILTER, routeConstants } from './constants';

/**
 * Which of the three list views `/dash/data` is showing, and which one it should show when the URL
 * does not say.
 *
 * The section used to answer both questions in four places — `DataSection`, `DataViewSwitcher`,
 * `DataContainer` and `DashboardContainer` — each reading the same localStorage key and reaching a
 * different answer. The route record now decides what renders, so one question is left, and it is
 * the one the URL cannot answer. What to show on arrival at `/dash/data`.
 */

/** The route each view is. Navigation targets these; nothing else picks a view route by hand. */
export const DATA_VIEW_ROUTE_NAME: Record<DATA_VIEW_FILTER, string> = {
  [DATA_VIEW_FILTER.CASES]: routeConstants.CASES.name,
  [DATA_VIEW_FILTER.ENTRIES]: routeConstants.ENTRIES.name,
  [DATA_VIEW_FILTER.PIPELINE]: routeConstants.CASES_PIPELINE.name
};

/**
 * The view a route shows, or `undefined` for anything that is not one of the three.
 *
 * `cases/entries` and `entries` are both the entries view. Only `entries` is navigated to now; the
 * older URL is still read because it is in people's history and their bookmarks.
 */
export const dataViewForRoute = (
  routeName?: string | symbol | null
): DATA_VIEW_FILTER | undefined => {
  switch (routeName) {
    case routeConstants.CASES.name:
      return DATA_VIEW_FILTER.CASES;
    case routeConstants.CASES_PIPELINE.name:
      return DATA_VIEW_FILTER.PIPELINE;
    case routeConstants.ENTRIES.name:
    case routeConstants.CASE_ENTRIES.name:
      return DATA_VIEW_FILTER.ENTRIES;
    default:
      return undefined;
  }
};

export interface DataViewDefaults {
  /** Whether the study groups entries into cases. One that does not has only the entries view. */
  longitudinal: boolean;
  /** The study's `flags.preferences.dataViewFilter`, set in study settings. */
  preference?: string | null;
  /** The view this browser last left the study on, written by the view switcher. */
  stored?: string | null;
}

/**
 * The view to show on arrival at `/dash/data`.
 *
 * The study setting beats what the browser remembers, which is what "you can change the default
 * mode from the study settings" promises on the switcher. Both are only preferences. A view the
 * study cannot show is skipped rather than obeyed, so a non-longitudinal study never lands on a
 * case list it has no cases for.
 */
export const defaultDataView = (
  { longitudinal, preference, stored }: DataViewDefaults
): DATA_VIEW_FILTER => {
  const available = longitudinal ?
    [DATA_VIEW_FILTER.CASES, DATA_VIEW_FILTER.ENTRIES, DATA_VIEW_FILTER.PIPELINE] :
    [DATA_VIEW_FILTER.ENTRIES];

  return [preference, stored].find(
    (wanted): wanted is DATA_VIEW_FILTER => available.includes(wanted as DATA_VIEW_FILTER)
  ) ?? available[0];
};
